import { defineStore } from "pinia"
import { apiV1 } from "@/config/axios"

export const useConnectorsStore = defineStore("connectors", {
  state: () => ({
    connectors: [],
    meta: null,
    selectedConnector: null,
    search: "",
    activeCategory: "all",
    loading: false,
    activating: false,
    error: null,
    nomenclatures: [],
    nomenclaturesMeta: null,
    nomenclatureStats: null,
    loadingNomenclatures: false,
    // Setup page state
    setupData: null,
    documentTypes: [],
    connectedAccounts: [],
    loadingSetup: false,
    loadingDocumentTypes: false
  }),

  getters: {
    filteredConnectors: (state) => {
      let filtered = state.connectors

      // Filter by category
      if (state.activeCategory && state.activeCategory !== "all") {
        filtered = filtered.filter(
          (connector) => connector.category === state.activeCategory
        )
      }

      // Filter by search
      if (state.search) {
        const query = state.search.toLowerCase().trim()
        filtered = filtered.filter(
          (connector) =>
            connector.name?.toLowerCase().includes(query) ||
            connector.description?.toLowerCase().includes(query)
        )
      }

      return filtered
    },

    categoryCounts: (state) => {
      return state.meta?.categories || {}
    },

    totalConnectors: (state) => {
      return state.meta?.total || 0
    },

    activeConnectors: (state) => {
      return state.connectors.filter(
        (connector) => connector.account_status === "active"
      )
    },

    // Available connectors are those NOT connected AND NOT was_connected (brand new)
    // Note: Inactive connectors are still CONNECTED, just paused
    availableConnectors: (state) => {
      return state.connectors.filter(
        (connector) => !connector.account_status && !connector.was_connected
      )
    },

    // Connected connectors — only active or error status (not pending/unactivated)
    connectedConnectors: (state) => {
      return state.connectors.filter((connector) =>
        ["active", "pending", "error"].includes(connector.account_status)
      )
    },

    // Disconnected connectors that can be reconnected
    disconnectedConnectors: (state) => {
      return state.connectors.filter(
        (connector) => !connector.account_status && connector.was_connected
      )
    }
  },

  actions: {
    setSearch(value) {
      this.search = value
    },

    setActiveCategory(category) {
      this.activeCategory = category
    },

    async fetchConnectors() {
      this.loading = true
      this.error = null
      try {
        const response = await apiV1.get("/connectors")
        this.connectors = response.data.data
        this.meta = response.data.meta
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async fetchConnector(connectorId) {
      this.error = null
      try {
        const response = await apiV1.get(`/connectors/${connectorId}`)
        this.selectedConnector = response.data.data
        return response.data.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      }
    },

    async activateConnector(connectorId, credentials = null) {
      this.activating = true
      this.error = null
      try {
        const payload = credentials ? { credentials } : {}
        const response = await apiV1.post(
          `/connectors/${connectorId}/activate`,
          payload
        )

        // Update the connector in the list with "pending" status
        // (backend sets status to "pending" initially, becomes "active" after sync job completes)
        const index = this.connectors.findIndex((c) => c.id === connectorId)
        if (index !== -1) {
          this.connectors[index] = {
            ...this.connectors[index],
            account_status: "pending",
            account_status_label: "Pending",
            activated_at: response.data.data?.activated_at
          }
        }

        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.activating = false
      }
    },

    // Disconnect a connector (soft delete)
    // The connection is soft-deleted and can be restored with reconnectConnector
    async disconnectConnector(connectorId) {
      this.error = null
      try {
        const response = await apiV1.delete(
          `/connectors/${connectorId}/disconnect`
        )

        // Remove account_status but mark as was_connected for re-connect option
        const index = this.connectors.findIndex((c) => c.id === connectorId)
        if (index !== -1) {
          this.connectors[index] = {
            ...this.connectors[index],
            account_status: null,
            account_status_label: null,
            activated_at: null,
            last_sync_at: null,
            was_connected: true
          }
        }

        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      }
    },

    // Reconnect a previously disconnected connector (restore from soft delete)
    async reconnectConnector(connectorId) {
      this.activating = true
      this.error = null
      try {
        const response = await apiV1.post(
          `/connectors/${connectorId}/reconnect`
        )

        // Update the connector with restored status
        const index = this.connectors.findIndex((c) => c.id === connectorId)
        if (index !== -1) {
          const data = response.data.data || {}
          this.connectors[index] = {
            ...this.connectors[index],
            account_status: data.status || "active",
            account_status_label:
              data.status === "active" ? "Active" : "Inactive",
            activated_at: data.activated_at,
            was_connected: false
          }
        }

        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.activating = false
      }
    },

    async toggleConnector(connectorId) {
      this.error = null
      try {
        const response = await apiV1.post(`/connectors/${connectorId}/toggle`)

        // Update the connector in the list with the new status
        const index = this.connectors.findIndex((c) => c.id === connectorId)
        if (index !== -1) {
          const newStatus = response.data.data?.status || "active"
          this.connectors[index] = {
            ...this.connectors[index],
            account_status: newStatus,
            account_status_label: newStatus === "active" ? "Active" : "Inactive"
          }
        }

        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      }
    },

    getConnectorById(connectorId) {
      return this.connectors.find((c) => c.id === connectorId)
    },

    clearError() {
      this.error = null
    },

    async fetchNomenclatures(connectorId, params = {}) {
      this.loadingNomenclatures = true
      this.error = null
      try {
        const response = await apiV1.get(
          `/connectors/${connectorId}/nomenclatures`,
          {
            params: {
              account_connector_id: params.accountConnectorId || null,
              type: params.type || null,
              search: params.search || null,
              per_page: params.perPage || 50,
              page: params.page || 1
            }
          }
        )
        this.nomenclatures = response.data.data
        this.nomenclaturesMeta = response.data.meta
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.loadingNomenclatures = false
      }
    },

    async fetchNomenclatureStats(connectorId, accountConnectorId = null) {
      this.error = null
      try {
        const response = await apiV1.get(
          `/connectors/${connectorId}/nomenclatures/stats`,
          {
            params: {
              account_connector_id: accountConnectorId || null
            }
          }
        )
        this.nomenclatureStats = response.data.data
        return response.data.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      }
    },

    clearNomenclatures() {
      this.nomenclatures = []
      this.nomenclaturesMeta = null
      this.nomenclatureStats = null
    },

    async fetchSetupData(connectorId) {
      this.loadingSetup = true
      this.error = null
      try {
        const response = await apiV1.get(`/connectors/${connectorId}/setup`)
        const payload = response.data.data
        this.setupData = payload
        this.selectedConnector = payload.connector ?? null
        if (this.selectedConnector == null) {
          try {
            await this.fetchConnector(connectorId)
          } catch {
            this.selectedConnector = null
          }
        }
        this.nomenclatureStats = payload.nomenclature_stats || null
        this.connectedAccounts = payload.connected_accounts || []
        return payload
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.loadingSetup = false
      }
    },

    async fetchDocumentTypes(
      connectorId,
      accountConnectorId = null,
      { all = false } = {}
    ) {
      this.loadingDocumentTypes = true
      this.error = null
      try {
        const params = {}
        if (all) {
          params.all = 1
        } else if (accountConnectorId) {
          params.account_connector_id = accountConnectorId
        }
        const response = await apiV1.get(
          `/connectors/${connectorId}/document-types`,
          { params }
        )
        this.documentTypes = response.data.data
        return response.data.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.loadingDocumentTypes = false
      }
    },

    async fetchAccountsWithDocumentTypes(connectorId) {
      try {
        const response = await apiV1.get(
          `/connectors/${connectorId}/accounts-with-document-types`
        )
        return response.data.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      }
    },

    async syncAccountConnector(connectorId, accountConnectorId) {
      try {
        const response = await apiV1.post(
          `/connectors/${connectorId}/account-connectors/${accountConnectorId}/sync`
        )
        // Update the account connector in the local state
        const updated = response.data.data
        const index = this.connectedAccounts.findIndex(
          (ac) => ac.id === accountConnectorId
        )
        if (index !== -1) {
          this.connectedAccounts[index] = updated
        }
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      }
    },

    clearSetupData() {
      this.setupData = null
      this.documentTypes = []
      this.nomenclatureStats = null
      this.connectedAccounts = []
    }
  }
})
